// FieldVision - MarkDetaljePage.tsx
// Mark detalje side med split-view layout: sidebar + Leaflet kort
// FIX: Frontend fallback - proever begge Marknr formater hvis foerste kald returnerer tomt
// FIX: Haandterer satellit kort GeoJson korrekt
// FIX: Parser microservice FeatureCollection format korrekt (hver feature = en zone)
// NEW: Support for delete med refresh
// FIX: Sender nu CVR parameter til getZoneMapsByField for at undgaa cross-CVR data (2025-12-30)
// NEW: Tildelingslogfiler tab med upload funktionalitet (2025-01-10)
// NEW: Vis application log GPS polygon på Leaflet kort (2025-01-10)

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import MarkDropdown from './MarkDropdown';
import NoteField from './NoteField';
import KortHistorik from './KortHistorik';
import ZoneMapView from './ZoneMapView';
import ZoneLegend from './ZoneLegend';
import apiService from '../services/api';
import { ZoneData } from './zones/ZoneRow';
import ApplicationLogCard from './ApplicationLogCard';
import ApplicationLogUploadModal from './ApplicationLogUploadModal';
import SprayJournalSection from './SprayJournalSection';


export const PRODUCT_LABELS: Record<string, string> = {
    nitrogen: 'Kvælstof (N)',
    phosphor: 'Fosfor (P)',
    potassium: 'Kalium (K)',
    npk: 'NPK',
    pesticide: 'Pesticid',
    herbicide: 'Herbicid',
    fungicide: 'Fungicid',
    growth: 'Vækstregulering',
    lime: 'Kalk',
    other: 'Andet'
};

export const ZONE_COLORS: Record<number, string> = {
  1: '#F61F1F',
  2: '#F68C1F',
  3: '#F6C81F',
  4: '#B9F61F',
  5: '#26F61F',
};

interface FieldMap {
  Id: number;
  FieldId: number | null;
  OrderId: number | null;
  Type: string;
  Source: string;
  CaptureDate: string;
  Status: string;
  ZonesJson?: string;
  Zones?: any;
  GeoJson?: any; // FIX: Tilfoej GeoJson property for satellit kort
  ThumbnailPath?: string;
  CreatedAt: string;
  Marknr: string;
  Areal: number | null;
  CVR: string;
}

interface Field {
  id?: string | number;
  marknr: string;
  areal: number;
  cvr: string;
  afgrode?: string;
  mapCount?: number;
  geometry?: string;
}

interface Order {
  orderId: number;
  orderNumber?: string;
  serviceType?: string;
  status?: string;
  fieldNames?: string[];
}

interface ApplicationLog {
  Id: number;
  ApplicationDate: string;
  ProductType: string;
  OriginalFilename: string;
  DetectedTractor: string | null;
  DetectedEquipment: string | null;
  DetectedDurationMinutes: number | null;
  DetectedUnit: string | null;
  GeometryMatchPercent: number | null;
  CreatedAt: string;
}

interface MarkDetaljePageProps {
  fieldId?: string;
  orderId?: number;
  field?: Field;
  order?: Order;
  fields?: Field[];
  onBack: () => void;
  onFieldChange?: (fieldId: string) => void;
  onNavigateToBooking?: () => void;
}

const MarkDetaljePage: React.FC<MarkDetaljePageProps> = ({
  fieldId,
  orderId,
  field,
  order,
  fields = [],
  onBack,
  onFieldChange,
  onNavigateToBooking
}) => {
  const [maps, setMaps] = useState<FieldMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeMapId, setActiveMapId] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [currentMarknr, setCurrentMarknr] = useState<string | null>(null);
  const [deleteMessage, setDeleteMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'kort' | 'logs'>('kort');
  const [applicationLogs, setApplicationLogs] = useState<ApplicationLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedLogGeometry, setSelectedLogGeometry] = useState<any>(null);
  const [selectedLogGeoJson, setSelectedLogGeoJson] = useState<any>(null);
  const [selectedLogZones, setSelectedLogZones] = useState<ZoneData[]>([]);
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null);
  const [openLogMenuId, setOpenLogMenuId] = useState<number | null>(null);
  const [editingProductTypeLogId, setEditingProductTypeLogId] = useState<number | null>(null);

  // Beregn mark-centroid fra geometry (til DMI vejr i sprøjtejournal)
  const fieldCentroid = useMemo(() => {
    try {
      const geomStr = field?.geometry;
      if (!geomStr) return null;
      const geom = typeof geomStr === 'string' ? JSON.parse(geomStr) : geomStr;
      let coords: number[][] = [];
      if (geom.type === 'Polygon') {
        coords = geom.coordinates[0];
      } else if (geom.type === 'MultiPolygon') {
        coords = geom.coordinates[0][0];
      } else if (geom.coordinates) {
        coords = Array.isArray(geom.coordinates[0][0]) ? geom.coordinates[0] : geom.coordinates;
      }
      if (coords.length === 0) return null;
      const sumLon = coords.reduce((s: number, c: number[]) => s + c[0], 0);
      const sumLat = coords.reduce((s: number, c: number[]) => s + c[1], 0);
      return { lat: sumLat / coords.length, lon: sumLon / coords.length };
    } catch {
      return null;
    }
  }, [field?.geometry]);

  useEffect(() => {
    console.log('=== MarkDetaljePage useEffect ===');
    console.log('fieldId:', fieldId);
    console.log('fieldId type:', typeof fieldId);
    console.log('orderId:', orderId);
    console.log('field prop:', field);
    console.log('field.cvr:', field?.cvr);
    loadMaps();
  }, [fieldId, orderId]);

  /**
   * FIX: Proever begge Marknr formater hvis foerste kald returnerer tomt
   * Backend burde haandtere begge formater, men som fallback proever vi begge fra frontend
   * FIX: Sender nu CVR parameter for at filtrere kort til specifik mark
   */
  const loadMaps = useCallback(async () => {
    console.log('=== MarkDetaljePage loadMaps START ===');
    console.log('fieldId:', fieldId);
    console.log('orderId:', orderId);
    console.log('field?.cvr:', field?.cvr);

    try {
      setLoading(true);
      setError(null);

      let allMaps: FieldMap[] = [];
      let marknr: string | null = null;

      // FIX: Hent CVR fra field prop hvis tilgaengelig
      const cvr = field?.cvr;

      if (orderId) {
        console.log('loadMaps: Loading via orderId', orderId);

        const orderMaps = await apiService.getZoneMapsByOrder(orderId);
        console.log('loadMaps: orderMaps response:', orderMaps);

        if ((orderMaps as any[]).length > 0) {
          marknr = (orderMaps as any[])[0].Marknr;
          console.log('loadMaps: Got marknr from order:', marknr);
          setCurrentMarknr(marknr);

          // FIX: Når vi har orderId, brug KUN orderMaps - ikke bland med andre kort
          allMaps = orderMaps as FieldMap[];
        }
      } else if (fieldId) {
        console.log('loadMaps: Loading via fieldId (Marknr):', fieldId, 'cvr:', cvr);
        setCurrentMarknr(fieldId);

        // FIX: Proev foerst med det originale fieldId og CVR
        console.log('loadMaps: Trying with original fieldId:', fieldId, 'cvr:', cvr);
        let fieldMaps = await apiService.getZoneMapsByField(fieldId as any, cvr);
        console.log('loadMaps: First API response:', fieldMaps);

        // FIX: Hvis tomt resultat, proev med "Mark " prefix
        if (!Array.isArray(fieldMaps) || fieldMaps.length === 0) {
          // Tjek om fieldId allerede har "Mark " prefix
          if (!fieldId.startsWith('Mark ')) {
            const marknrWithPrefix = 'Mark ' + fieldId;
            console.log('loadMaps: No results, trying with prefix:', marknrWithPrefix, 'cvr:', cvr);
            fieldMaps = await apiService.getZoneMapsByField(marknrWithPrefix as any, cvr);
            console.log('loadMaps: Prefixed API response:', fieldMaps);
          }
        }

        // FIX: Hvis stadig tomt og fieldId har "Mark " prefix, proev uden
        if (!Array.isArray(fieldMaps) || fieldMaps.length === 0) {
          if (fieldId.startsWith('Mark ')) {
            const marknrWithoutPrefix = fieldId.replace(/^Mark /, '');
            console.log('loadMaps: No results, trying without prefix:', marknrWithoutPrefix, 'cvr:', cvr);
            fieldMaps = await apiService.getZoneMapsByField(marknrWithoutPrefix as any, cvr);
            console.log('loadMaps: Unprefixed API response:', fieldMaps);
          }
        }

        allMaps = Array.isArray(fieldMaps) ? fieldMaps as FieldMap[] : [];
        console.log('loadMaps: Final allMaps.length:', allMaps.length);
      }

      // Sorter kort
      allMaps.sort((a: any, b: any) => {
        const typeOrder: Record<string, number> = {
          'NDVI': 1, 'ndvi': 1,
          'NDRE': 1, 'ndre': 1, // FIX: Tilfoej NDRE for satellit
          'weed': 2, 'Weed': 2, 'Ukrudt': 2,
          'regrowth': 3, 'Regrowth': 3
        };
        const typeA = typeOrder[(a as any).Type] || 99;
        const typeB = typeOrder[(b as any).Type] || 99;
        if (typeA !== typeB) return typeA - typeB;

        const dateA = new Date((a as any).CreatedAt || (a as any).CaptureDate).getTime();
        const dateB = new Date((b as any).CreatedAt || (b as any).CaptureDate).getTime();
        return dateB - dateA;
      });

      console.log('loadMaps: Setting maps state with', allMaps.length, 'maps');
      setMaps(allMaps);

      if (allMaps.length > 0) {
        const orderMap = orderId
          ? allMaps.find((m: any) => (m as any).OrderId === orderId)
          : null;
        setActiveMapId((orderMap as any)?.Id || (allMaps[0] as any).Id);
        console.log('loadMaps: Set activeMapId to:', (orderMap as any)?.Id || (allMaps[0] as any).Id);
      } else {
        setActiveMapId(null);
        console.log('loadMaps: No maps found, activeMapId = null');
      }

      console.log('=== MarkDetaljePage loadMaps END ===');
    } catch (err) {
      console.error('loadMaps ERROR:', err);
      setError('Kunne ikke hente kort');
    } finally {
      setLoading(false);
    }
  }, [fieldId, orderId, field?.cvr]);

  // Load application logs
  const loadApplicationLogs = useCallback(async () => {
    if (!field?.cvr || !currentMarknr) return;

    setLogsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:4002'}/api/application-logs/field/${field.cvr}/${currentMarknr}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setApplicationLogs(data);
      }
    } catch (err) {
      console.error('Error loading application logs:', err);
    } finally {
      setLogsLoading(false);
    }
  }, [field?.cvr, currentMarknr]);

  // Load logs count on mount (for badge display)
  useEffect(() => {
    loadApplicationLogs();
  }, [loadApplicationLogs]);

  // Load logs when tab changes
  useEffect(() => {
    if (activeTab === 'logs') {
      loadApplicationLogs();
    } else {
      // Clear selected log geometry when switching to kort tab
      setSelectedLogGeometry(null);
    }
  }, [activeTab, loadApplicationLogs]);

  // Auto-vælg første log når logs er loaded og vi er på logs tab
  useEffect(() => {
    if (activeTab === 'logs' && applicationLogs.length > 0 && selectedLogZones.length === 0) {
      handleLogView(applicationLogs[0].Id);
    }
  }, [activeTab, applicationLogs]);

  const activeMap = useMemo(() => {
    return maps.find((m: any) => (m as any).Id === activeMapId) || null;
  }, [maps, activeMapId]);

  /**
   * Parse zones fra forskellige formater:
   * 1. Array direkte: [{ zone: 1, hectares: ... }, ...]
   * 2. Object med zones property: { zones: [...] }
   * 3. Object med geojson.features: { geojson: { features: [...] } }
   * 4. FeatureCollection med zones i features[0].properties.zones (gammel satellit)
   * 5. FeatureCollection hvor hver feature ER en zone (ny microservice format)
   */
  const activeZones = useMemo((): ZoneData[] => {
    if (!activeMap) return [];

    let zones: ZoneData[] = [];

    // Helper function to parse FeatureCollection where each feature is a zone
    const parseFeatureCollectionZones = (fc: any): ZoneData[] => {
      if (!fc.features || !Array.isArray(fc.features)) return [];

      // Check if features have zone property (microservice format)
      const hasZoneProperty = fc.features.some((f: any) =>
        f.properties && typeof f.properties.zone === 'number'
      );

      if (hasZoneProperty) {
        console.log('activeZones: Parsing microservice FeatureCollection format (each feature = zone)');
        return fc.features
          .filter((f: any) => f.properties && typeof f.properties.zone === 'number')
          .map((f: any) => ({
            zone: f.properties.zone,
            hectares: f.properties.hectares || 0,
            rate: f.properties.rate || 0,
            color: f.properties.color || ZONE_COLORS[f.properties.zone],
            ndreMin: f.properties.ndreMin,
            ndreMax: f.properties.ndreMax
          }));
      }

      return [];
    };

    if ((activeMap as any).Zones) {
      if (Array.isArray((activeMap as any).Zones)) {
        zones = (activeMap as any).Zones;
      } else if ((activeMap as any).Zones.zones) {
        zones = (activeMap as any).Zones.zones;
      } else if ((activeMap as any).Zones.geojson?.features) {
        zones = (activeMap as any).Zones.geojson.features.map((f: any) => ({
          zone: (f as any).properties.zone,
          rate: (f as any).properties.rate,
          rateInt: (f as any).properties.rateInt,
          unit: (f as any).properties.unit || 'l/ha',
          hectares: (f as any).properties.hectares,
        }));
      }
      // FIX: Check for FeatureCollection where each feature is a zone (microservice format)
      else if ((activeMap as any).Zones.type === 'FeatureCollection') {
        zones = parseFeatureCollectionZones((activeMap as any).Zones);
      }
    } else if ((activeMap as any).ZonesJson) {
      try {
        const parsed = JSON.parse((activeMap as any).ZonesJson);
        if (Array.isArray(parsed)) {
          zones = parsed;
        } else if (parsed.zones) {
          zones = parsed.zones;
        } else if (parsed.geojson?.features) {
          zones = parsed.geojson.features.map((f: any) => ({
            zone: (f as any).properties.zone,
            rate: (f as any).properties.rate,
            rateInt: (f as any).properties.rateInt,
            unit: (f as any).properties.unit || 'l/ha',
            hectares: (f as any).properties.hectares,
          }));
        }
        // FIX: Haandter satellit format - FeatureCollection med zones i properties
        else if (parsed.type === 'FeatureCollection' && parsed.features?.[0]?.properties?.zones) {
          zones = parsed.features[0].properties.zones;
        }
        // FIX: Haandter ny microservice format - FeatureCollection hvor hver feature er en zone
        else if (parsed.type === 'FeatureCollection') {
          zones = parseFeatureCollectionZones(parsed);
        }
      } catch (e) {
        console.error('Error parsing ZonesJson:', e);
      }
    }

    // FIX: Also check GeoJson property (satellite maps may store zones there)
    if (zones.length === 0 && (activeMap as any).GeoJson) {
      const geoJson = (activeMap as any).GeoJson;
      if (geoJson.type === 'FeatureCollection') {
        zones = parseFeatureCollectionZones(geoJson);
      }
    }

    console.log('activeZones: Final parsed zones:', zones.length, 'zones');
    return zones.filter((z: any) => (z as any).zone !== 0).sort((a: any, b: any) => (a as any).zone - (b as any).zone);
  }, [activeMap]);

  // FIX: Opdateret activeGeoJson memo til at haandtere satellit kort
  const activeGeoJson = useMemo(() => {
    if (!activeMap) return null;

    // FIX: Check GeoJson property first (satellite format - backend setter dette)
    if ((activeMap as any).GeoJson) {
      console.log('activeGeoJson: Using activeMap.GeoJson (satellite format)');
      return (activeMap as any).GeoJson;
    }

    // Check Zones.geojson (drone format)
    if ((activeMap as any).Zones?.geojson) {
      console.log('activeGeoJson: Using activeMap.Zones.geojson (drone format)');
      return (activeMap as any).Zones.geojson;
    }

    // FIX: Check if Zones itself is a FeatureCollection (microservice format)
    if ((activeMap as any).Zones?.type === 'FeatureCollection') {
      console.log('activeGeoJson: Using activeMap.Zones as FeatureCollection (microservice format)');
      return (activeMap as any).Zones;
    }

    // Parse ZonesJson
    if ((activeMap as any).ZonesJson) {
      try {
        const parsed = JSON.parse((activeMap as any).ZonesJson);

        // Check for nested geojson property (drone format)
        if (parsed.geojson) {
          console.log('activeGeoJson: Using parsed.geojson (drone format)');
          return parsed.geojson;
        }

        // FIX: If it's a FeatureCollection, return it directly (satellite format)
        if (parsed.type === 'FeatureCollection') {
          console.log('activeGeoJson: Using parsed FeatureCollection directly (satellite format)');
          return parsed;
        }
      } catch (e) {
        console.error('Error parsing ZonesJson for geojson:', e);
      }
    }

    console.log('activeGeoJson: No GeoJson found');
    return null;
  }, [activeMap]);

  // Byg field outline GeoJSON naar ingen tildelingskort
  const fieldOutlineGeoJson = useMemo(() => {
    if (activeGeoJson) return null;
    if (!(field as any)?.geometry) return null;

    try {
      const geometry = typeof (field as any).geometry === 'string'
        ? JSON.parse((field as any).geometry)
        : (field as any).geometry;

      return {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {
            marknr: (field as any).marknr,
            areal: (field as any).areal,
            isOutline: true
          },
          geometry: geometry
        }]
      };
    } catch (e) {
      console.error('Error parsing field geometry:', e);
      return null;
    }
  }, [activeGeoJson, field]);

  const handleMapSelect = (mapId: number) => {
    setActiveMapId(mapId);
    setSelectedLogGeoJson(null);
    setSelectedLogZones([]);
  };

  const handleZonesChange = (updatedZones: ZoneData[]) => {
    setMaps(prev => prev.map((m: any) => {
      if ((m as any).Id !== activeMapId) return m;

      // FIX: Handle microservice format - update zones in FeatureCollection features
      if ((m as any).Zones?.type === 'FeatureCollection' && (m as any).Zones.features) {
        const updatedFeatures = (m as any).Zones.features.map((f: any) => {
          const updatedZone = updatedZones.find(z => z.zone === f.properties?.zone);
          if (updatedZone) {
            return {
              ...f,
              properties: {
                ...f.properties,
                rate: updatedZone.rate
              }
            };
          }
          return f;
        });
        return {
          ...m,
          Zones: {
            ...(m as any).Zones,
            features: updatedFeatures
          }
        };
      }

      if (Array.isArray((m as any).Zones)) {
        return { ...m, Zones: updatedZones };
      } else if ((m as any).Zones?.zones) {
        return { ...m, Zones: { ...(m as any).Zones, zones: updatedZones } };
      }
      return m;
    }));
  };

  // Haandter kort sletning
  const handleMapDelete = useCallback((mapId: number, deleteType: 'hard' | 'soft') => {
    console.log('handleMapDelete:', mapId, deleteType);

    // Vis besked
    const message = deleteType === 'hard'
      ? 'Kortet er slettet permanent'
      : 'Kortet er flyttet til papirkurven';
    setDeleteMessage(message);

    // Fjern besked efter 3 sekunder
    setTimeout(() => {
      setDeleteMessage(null);
    }, 3000);

    // Fjern kortet fra listen
    setMaps(prevMaps => {
      const newMaps = prevMaps.filter(m => m.Id !== mapId);

      // Hvis det slettede kort var aktivt, vaelg et nyt
      if (activeMapId === mapId && newMaps.length > 0) {
        setActiveMapId(newMaps[0].Id);
      } else if (newMaps.length === 0) {
        setActiveMapId(null);
      }

      return newMaps;
    });
  }, [activeMapId]);

  // Bestil knap handler
  const handleBestilClick = () => {
    if (onNavigateToBooking) {
      onNavigateToBooking();
    }
  };

  // Application log handlers
  const handleLogView = async (id: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:4002'}/api/application-logs/${id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        
        // Parse ZonesJson for heatmap visualization
        if (data.ZonesJson) {
          try {
            const zonesData = typeof data.ZonesJson === 'string' 
              ? JSON.parse(data.ZonesJson) 
              : data.ZonesJson;
            
            if (zonesData.zones && zonesData.zones.length > 0) {
              // Byg GeoJSON fra zone polygoner
              const geoJson = {
                type: 'FeatureCollection',
                features: zonesData.zones.map((z: any) => ({
                  type: 'Feature',
                  properties: {
                    zone: z.zone,
                    rate: z.rate,
                    hectares: z.hectares,
                    color: z.color
                  },
                  geometry: z.polygon
                }))
              };
              
              // Byg zones array for legend
              const zones: ZoneData[] = zonesData.zones.map((z: any) => ({
                zone: z.zone,
                rate: z.rate,
                hectares: z.hectares,
                color: z.color,
                massKg: z.massKg
              }));
              
              setSelectedLogGeoJson(geoJson);
              setSelectedLogZones(zones);
              setSelectedLogGeometry(null);
              console.log('handleLogView: Loaded', zones.length, 'zones from ZonesJson');
              return;
            }
          } catch (e) {
            console.error('Error parsing ZonesJson:', e);
          }
        }
        
        // Fallback: Vis kun geometry outline
        if (data.Geometry) {
          const geom = typeof data.Geometry === 'string'
            ? JSON.parse(data.Geometry)
            : data.Geometry;
          setSelectedLogGeometry({
            type: 'FeatureCollection',
            features: [{
              type: 'Feature',
              properties: { type: 'applicationLog' },
              geometry: geom
            }]
          });
          setSelectedLogGeoJson(null);
          setSelectedLogZones([]);
        }
      }
    } catch (error) {
      console.error('Error fetching log:', error);
    }
  };

  const handleProductTypeChange = async (logId: number, newType: string) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:4002'}/api/application-logs/${logId}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ productType: newType })
        }
      );
      if (res.ok) {
        // Opdater lokal state
        setApplicationLogs(prev => prev.map(log =>
          log.Id === logId ? { ...log, ProductType: newType } : log
        ));
      }
    } catch (err) {
      console.error('Fejl ved opdatering af produkttype:', err);
    } finally {
      setEditingProductTypeLogId(null);
    }
  };

  const handleLogDelete = async (id: number) => {
    if (!window.confirm('Er du sikker på at du vil slette denne log?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:4002'}/api/application-logs/${id}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.ok) {
        setApplicationLogs(prev => prev.filter(l => l.Id !== id));
        // Clear geometry if deleted log was selected
        setSelectedLogGeometry(null);
      }
    } catch (err) {
      console.error('Error deleting log:', err);
    }
  };

  const handleLogDownload = async (id: number) => {
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_API_URL || 'http://localhost:4002'}/api/application-logs/${id}/download`;

    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (response.ok) {
      const blob = await response.blob();
      const filename = response.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] || 'download.zip';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
    }
  };

  const currentField = useMemo(() => {
    // Når man kommer fra en ordre, vis "Ordre #X" i stedet for marknr
    if (orderId) {
      return {
        id: `ordre-${orderId}`,
        marknr: `Ordre #${orderId}`,
        areal: 0,
        cvr: '',
        mapCount: maps.length
      };
    }

    if (field) {
      return {
        id: (field as any).id?.toString() || fieldId || currentMarknr || '',
        marknr: (field as any).marknr || currentMarknr || '',
        areal: (field as any).areal || (maps.length > 0 ? (maps[0] as any).Areal || 0 : 0),
        cvr: (field as any).cvr || (maps.length > 0 ? (maps[0] as any).CVR : ''),
        mapCount: maps.length
      };
    }

    if (maps.length > 0) {
      return {
        id: fieldId || currentMarknr || '',
        marknr: currentMarknr || (maps[0] as any).Marknr || '',
        areal: (maps[0] as any).Areal || 0,
        cvr: (maps[0] as any).CVR || '',
        mapCount: maps.length
      };
    }

    if (fieldId) {
      return {
        id: fieldId,
        marknr: fieldId,
        areal: (field as any)?.areal || 0,
        cvr: (field as any)?.cvr || '',
        mapCount: 0
      };
    }

    return null;
  }, [field, maps, fieldId, currentMarknr]);

  return (
    <div className="mark-detalje-container">
      <aside className="mark-detalje-sidebar">
        <div className="sidebar-header">
          <button className="back-link" onClick={onBack}>
            <span className="back-arrow">&larr;</span>
            Mine marker
          </button>
        </div>

        <div className="sidebar-section">
          <MarkDropdown
            currentField={currentField as any}
            fields={fields as any}
            onFieldChange={onFieldChange}
          />
        </div>

        <div className="sidebar-section">
          <NoteField
            value={note}
            onChange={setNote}
            placeholder="Skriv en note til marken, fx afgrøde..."
          />
        </div>

        {/* Tabs */}
        <div className="tabs-container">
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'kort' ? 'active' : ''}`}
              onClick={() => { setActiveTab('kort'); setSelectedLogGeoJson(null); setSelectedLogZones([]); }}
            >
              Tildelingskort
              <span className="tab-badge">{maps.length}</span>
            </button>
            <button
              className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
              onClick={() => setActiveTab('logs')}
            >
              Tildelingslogfiler
              <span className="tab-badge">{applicationLogs.length}</span>
            </button>
          </div>
        </div>

        {/* Tab actions */}
        <div className="tab-actions">
          {activeTab === 'kort' ? (
            <button className="bestil-btn" onClick={handleBestilClick}>
              Bestil kort
            </button>
          ) : (
            <button className="bestil-btn" onClick={() => setShowUploadModal(true)}>
              Upload log
            </button>
          )}
        </div>

        {/* Delete besked */}
        {deleteMessage && (
          <div className="delete-message">
            <span className="delete-message-icon">&#10003;</span>
            {deleteMessage}
          </div>
        )}

        <div className="kort-liste-container">
          {activeTab === 'kort' ? (
            /* KORT TAB */
            loading ? (
              <div className="loading-state">
                <p>Indlaeser kort...</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <p>{error}</p>
                <button onClick={loadMaps} className="retry-btn">
                  Proev igen
                </button>
              </div>
            ) : maps.length === 0 ? (
              <div className="empty-state">
                <p>Der er endnu ikke lavet tildelingskort til denne mark.</p>
                <button className="order-btn" onClick={handleBestilClick}>
                  Bestil tildelingskort
                </button>
              </div>
            ) : (
              <KortHistorik
                maps={maps as any}
                activeMapId={activeMapId}
                onMapSelect={handleMapSelect}
                onZonesChange={handleZonesChange}
                onMapDelete={handleMapDelete}
              />
            )
          ) : (
            /* APPLICATION LOGS TAB */
            logsLoading ? (
              <div className="loading-state">
                <p>Indlaeser logfiler...</p>
              </div>
            ) : applicationLogs.length === 0 ? (
              <div className="empty-state">
                <p>Ingen tildelingslogfiler uploadet endnu.</p>
                <button className="order-btn" onClick={() => setShowUploadModal(true)}>
                  Upload logfil
                </button>
              </div>
            ) : (
              <div className="logs-list">
                {applicationLogs.map(log => (
                  <div key={log.Id} className="log-card-wrapper">
                    {/* Zone panel vises altid (ikke efter øje-klik) */}
                    {selectedLogZones.length > 0 && (
                      <div className="log-zone-panel">
                        {/* Header der ligner satellit-kortet */}
                        <div className="log-zone-header-satellite">
                          <div className="log-zone-thumbnail">
                            <svg viewBox="0 0 60 60" className="log-zone-svg">
                              <rect x="0" y="0" width="60" height="60" fill="#f3f4f6" />
                              {selectedLogZones.map((zone, index) => {
                                const segmentHeight = 60 / selectedLogZones.length;
                                return (
                                  <rect
                                    key={zone.zone}
                                    x="5"
                                    y={5 + index * segmentHeight}
                                    width="50"
                                    height={segmentHeight - 2}
                                    fill={ZONE_COLORS[zone.zone]}
                                    rx="2"
                                  />
                                );
                              })}
                            </svg>
                          </div>
                          <div className="log-zone-header-info">
                            <div className="log-zone-header-top">
                              {editingProductTypeLogId === log.Id ? (
                                <select
                                  className="log-zone-product-select"
                                  value={log.ProductType}
                                  onChange={(e) => handleProductTypeChange(log.Id, e.target.value)}
                                  onBlur={() => setEditingProductTypeLogId(null)}
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {Object.entries(PRODUCT_LABELS).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                  ))}
                                </select>
                              ) : (
                                <span
                                  className="log-zone-product log-zone-product-editable"
                                  onClick={(e) => { e.stopPropagation(); setEditingProductTypeLogId(log.Id); }}
                                  title="Klik for at ændre produkttype"
                                >
                                  {PRODUCT_LABELS[log.ProductType] || log.ProductType} <span className="log-zone-product-edit-icon">✎</span>
                                </span>
                              )}
                              <span className="log-zone-badge">Tildelingslog</span>
                            </div>
                            <div className="log-zone-header-bottom">
                              <span className="log-zone-date">📅 {new Date(log.ApplicationDate).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                              <span className="log-zone-count-header">{selectedLogZones.length} zoner</span>
                            </div>
                            {(log.DetectedTractor || log.DetectedEquipment || log.DetectedDurationMinutes) && (
                              <div className="log-zone-machine">
                                🚜 {[log.DetectedTractor, log.DetectedEquipment].filter(Boolean).join(' · ')}
                                {log.DetectedDurationMinutes && ` · ${log.DetectedDurationMinutes} min`}
                              </div>
                            )}
                          </div>
                          {/* Tre-prikker menu */}
                          <div className="log-menu-container">
                            <button
                              className="log-menu-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenLogMenuId(openLogMenuId === log.Id ? null : log.Id);
                              }}
                              title="Flere handlinger"
                            >
                              &#8942;
                            </button>
                            {openLogMenuId === log.Id && (
                              <div className="log-menu-dropdown">
                                <button 
                                  className="log-menu-item delete" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenLogMenuId(null);
                                    handleLogDelete(log.Id);
                                  }}
                                >
                                  🗑 Slet
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Foldbar header som ZonePanel */}
                        <button
                          className="log-zone-fold-header"
                          onClick={() => setExpandedLogId(expandedLogId === log.Id ? null : log.Id)}
                        >
                          <span className="log-fold-title">Anvendte mængder</span>
                          <div className="log-fold-right">
                            {expandedLogId !== log.Id && (
                              <span className="log-fold-summary">
                                {selectedLogZones.length} zoner | {Math.round(selectedLogZones.reduce((sum, z) => sum + (z.massKg || z.rate * (z.hectares || 0)), 0))} {selectedLogZones[0]?.unit === 'l/ha' ? 'l' : 'kg'}
                              </span>
                            )}
                            <span className={`log-fold-icon ${expandedLogId === log.Id ? 'expanded' : ''}`}>
                              ▼
                            </span>
                          </div>
                        </button>
                        
                        {expandedLogId === log.Id && (
                          <>
                            <div className="log-zone-table">
                              <div className="log-zone-unit-display">
                                <span>{selectedLogZones[0]?.unit || 'kg/ha'}</span>
                              </div>
                              {selectedLogZones.map(zone => (
                                <div key={zone.zone} className="log-zone-row">
                                  <div 
                                    className="log-zone-color" 
                                    style={{ backgroundColor: ZONE_COLORS[zone.zone] }}
                                  />
                                  <span className="log-zone-label">Zone {zone.zone}</span>
                                  <span className="log-zone-rate-box">{Math.round(zone.rate)}</span>
                                  <span className="log-zone-unit">{zone.unit || 'kg/ha'}</span>
                                  <span className="log-zone-hectares">{zone.hectares?.toFixed(1)} ha</span>
                                  <span className="log-zone-total">{Math.round((zone.massKg || zone.rate * (zone.hectares || 0)))} {zone.unit === 'l/ha' ? 'l' : 'kg'}</span>
                                </div>
                              ))}
                            </div>
                            <div className="log-zone-totals">
                              <span>Total areal:</span>
                              <span>{selectedLogZones.reduce((sum, z) => sum + (z.hectares || 0), 0).toFixed(1)} ha</span>
                            </div>
                            <div className="log-zone-totals">
                              <span>Total mængde:</span>
                              <span>{Math.round(selectedLogZones.reduce((sum, z) => sum + (z.massKg || z.rate * (z.hectares || 0)), 0))} {selectedLogZones[0]?.unit === 'l/ha' ? 'l' : 'kg'}</span>
                            </div>
                          </>
                        )}
                        {/* Sprøjtejournal */}
                        <SprayJournalSection
                          logId={log.Id}
                          fieldLat={fieldCentroid?.lat}
                          fieldLon={fieldCentroid?.lon}
                          applicationDate={log.ApplicationDate}
                        />
                        <button 
                          className="log-download-btn"
                          onClick={() => handleLogDownload(log.Id)}
                        >
                          <span>⬇</span> Download original fil
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </aside>

      <main className="mark-detalje-map">
        <ZoneMapView key={selectedLogGeoJson ? 'log' : activeMapId}
          geoJson={selectedLogGeoJson || activeGeoJson || fieldOutlineGeoJson}
          zones={selectedLogGeoJson ? selectedLogZones : activeZones}
          zoneColors={ZONE_COLORS}
          emptyState={!selectedLogGeoJson && !activeGeoJson && !!fieldOutlineGeoJson}
          overlayGeometry={selectedLogGeometry}
        />

        {(selectedLogZones.length > 0 || activeZones.length > 0) && (
          <ZoneLegend
            zones={selectedLogZones.length > 0 ? selectedLogZones : activeZones as any}
            zoneColors={ZONE_COLORS}
            mapType={selectedLogZones.length > 0 ? 'LOG' : ((activeMap as any)?.Type || 'NDVI')}
          />
        )}
      </main>

      {/* Upload Modal */}
      {showUploadModal && (
        <ApplicationLogUploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          cvr={field?.cvr || ''}
          marknr={currentMarknr || ''}
          onUploadSuccess={() => {
            setShowUploadModal(false);
            loadApplicationLogs();
          }}
        />
      )}

      <style>{`
        .mark-detalje-container {
          display: flex;
          height: 100vh;
          background: #f9fafb;
        }

        .mark-detalje-sidebar {
          width: 380px;
          background: #f9fafb;
          border-right: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .sidebar-header {
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
          background: white;
        }

        .back-link {
          display: flex;
          align-items: center;
          gap: 8px;
          background: none;
          border: none;
          color: #16a34a;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          padding: 0;
        }

        .back-link:hover {
          color: #15803d;
        }

        .back-arrow {
          font-size: 18px;
        }

        .sidebar-section {
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
          background: white;
        }

        .tabs-container {
          border-bottom: 1px solid #e5e7eb;
          background: white;
          padding: 0 20px;
        }

        .tabs {
          display: flex;
          gap: 0;
        }

        .tab {
          padding: 12px 16px;
          font-size: 14px;
          font-weight: 500;
          color: #6b7280;
          background: none;
          border: none;
          cursor: pointer;
          position: relative;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: color 0.2s;
        }

        .tab:hover {
          color: #374151;
        }

        .tab.active {
          color: #16a34a;
        }

        .tab.active::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 0;
          right: 0;
          height: 2px;
          background: #16a34a;
        }

        .tab-badge {
          background: #e5e7eb;
          color: #374151;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 500;
        }

        .tab.active .tab-badge {
          background: #dcfce7;
          color: #16a34a;
        }

        .tab-actions {
          display: flex;
          justify-content: flex-end;
          padding: 12px 20px;
          background: #f9fafb;
        }

        .kort-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          background: #f9fafb;
        }

        .kort-header h3 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .bestil-btn {
          padding: 6px 12px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-size: 13px;
          color: #16a34a;
          cursor: pointer;
          transition: all 0.2s;
        }

        .bestil-btn:hover {
          background: #f0fdf4;
          border-color: #16a34a;
        }

        .delete-message {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0 20px 12px;
          padding: 10px 14px;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 8px;
          color: #16a34a;
          font-size: 14px;
          font-weight: 500;
          animation: slideIn 0.2s ease;
        }

        .delete-message-icon {
          font-size: 16px;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .kort-liste-container {
          flex: 1;
          overflow-y: auto;
          padding: 0 20px 20px;
        }

        .loading-state,
        .error-state,
        .empty-state {
          padding: 40px 20px;
          text-align: center;
          color: #6b7280;
        }

        .empty-state p {
          margin-bottom: 16px;
        }

        .order-btn {
          padding: 10px 20px;
          background: #16a34a;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
        }

        .order-btn:hover {
          background: #15803d;
        }

        .retry-btn {
          margin-top: 12px;
          padding: 8px 16px;
          background: #16a34a;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
        }

        .retry-btn:hover {
          background: #15803d;
        }

        .logs-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .mark-detalje-map {
          flex: 1;
          position: relative;
          background: #e5e7eb;
        }

        .mark-detalje-map .leaflet-container {
          height: 100%;
          width: 100%;
        }
        /* Log Zone Panel styles */
        .log-zone-panel {
          padding: 12px 16px 16px 16px;
          background: white;
          border: 2px solid #3c6c34;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(60, 108, 52, 0.15);
        }
        
        /* Wrapper for log-card + zone-panel */
        .log-card-wrapper {
          /* Ingen ekstra border - panel har sin egen */
        }

        .log-zone-section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 13px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 12px;
          padding: 8px 12px;
          background: #f9fafb;
          border-radius: 6px;
          cursor: default;
        }

        .log-zone-count {
          font-size: 12px;
          color: #6b7280;
          font-weight: 500;
        }

        .log-zone-table {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .log-zone-unit-display {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 4px;
          font-size: 12px;
          color: #6b7280;
        }

        .log-zone-row {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 13px;
        }

        .log-zone-color {
          width: 16px;
          height: 16px;
          border-radius: 4px;
          flex-shrink: 0;
        }

        .log-zone-label {
          color: #374151;
          min-width: 55px;
        }

        .log-zone-rate-box {
          font-weight: 600;
          color: #111827;
          min-width: 50px;
          text-align: center;
          padding: 4px 8px;
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
        }

        .log-zone-unit {
          color: #6b7280;
          min-width: 35px;
        }

        .log-zone-hectares {
          color: #6b7280;
          min-width: 50px;
          text-align: right;
        }

        .log-zone-total {
          font-weight: 600;
          color: #16a34a;
          min-width: 60px;
          text-align: right;
        }

        .log-zone-totals {
          display: flex;
          justify-content: space-between;
          margin-top: 12px;
          padding-top: 8px;
          border-top: 1px solid #e5e7eb;
          font-size: 13px;
        }

        .log-zone-totals span:first-child {
          color: #6b7280;
        }

        .log-zone-totals span:last-child {
          font-weight: 600;
          color: #111827;
        }

        .log-menu-container {
          position: relative;
        }
        .log-menu-btn {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          padding: 4px 8px;
          color: #6b7280;
          border-radius: 4px;
        }
        .log-menu-btn:hover {
          background: #f3f4f6;
          color: #374151;
        }
        .log-menu-dropdown {
          position: absolute;
          top: 100%;
          right: 0;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          z-index: 100;
          min-width: 120px;
        }
        .log-menu-item {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 10px 14px;
          border: none;
          background: none;
          cursor: pointer;
          font-size: 14px;
          text-align: left;
        }
        .log-menu-item.delete {
          color: #dc2626;
        }
        .log-menu-item:hover {
          background: #f9fafb;
        }
        .log-download-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 16px;
          background: #3c6c34;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.15s;
          margin-top: 12px;
        }

        .log-download-btn:hover {
          background: #15803d;
        }
        
        /* Satellit-lignende header for log */
        .log-zone-header-satellite {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding-bottom: 12px;
          margin-bottom: 12px;
          border-bottom: 1px solid #e5e7eb;
        }
        
        .log-zone-svg {
          width: 100%;
          height: 100%;
        }
        
        .log-zone-thumbnail {
          width: 60px;
          height: 60px;
          border-radius: 8px;
          overflow: hidden;
          flex-shrink: 0;
        }
        
        .log-zone-thumb-bar {
          height: 8px;
          border-radius: 2px;
        }
        
        .log-zone-header-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        
        .log-zone-header-top {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .log-zone-product-editable { cursor: pointer; }
        .log-zone-product-editable:hover { color: #16a34a; }
        .log-zone-product-edit-icon {
          font-size: 11px; opacity: 0.3; margin-left: 3px;
        }
        .log-zone-product-editable:hover .log-zone-product-edit-icon {
          opacity: 0.8; color: #16a34a;
        }
        .log-zone-product-select {
          font-size: 14px; font-weight: 600; color: #111827;
          border: 2px solid #16a34a; border-radius: 4px;
          padding: 2px 4px; background: white; outline: none;
          max-width: 160px;
        }
        .log-zone-product {
          font-size: 15px;
          font-weight: 600;
          color: #111827;
        }
        
        .log-zone-badge {
          font-size: 11px;
          padding: 2px 8px;
          background: #dbeafe;
          color: #1e40af;
          border-radius: 4px;
          font-weight: 500;
        }
        
        .log-zone-header-bottom {
          display: flex;
          align-items: center;
          gap: 16px;
          font-size: 12px;
          color: #6b7280;
        }
        
        .log-zone-date {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        .log-zone-machine {
          font-size: 12px;
          color: #6b7280;
          margin-top: 4px;
        }
        
        .log-zone-count-header {
          color: #374151;
        }
        
        /* Skjul log-card header når zones vises */
        .log-card-wrapper .log-card {
          display: none;
        }
        
        /* Fold header (matcher ZonePanel præcist) */
        .log-zone-fold-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          padding: 10px 12px;
          background: white;
          border: 1px solid #3b82f6;
          border-radius: 6px;
          cursor: pointer;
          transition: background 0.15s;
          margin-top: 8px;
        }
        
        .log-zone-fold-header:hover {
          background: #f9fafb;
        }
        
        .log-fold-title {
          font-size: 13px;
          font-weight: 500;
          color: #374151;
        }
        
        .log-fold-right {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .log-fold-summary {
          font-size: 12px;
          color: #6b7280;
        }
        
        .log-fold-icon {
          font-size: 10px;
          color: #9ca3af;
          transition: transform 0.2s;
        }
        
        .log-fold-icon.expanded {
          transform: rotate(180deg);
        }

      `}</style>
    </div>
  );
};

export default MarkDetaljePage;
